import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { studentCalendarLabelWide } from "@/lib/utils-app";
import { AlertTriangle, CheckCircle2, XCircle, GraduationCap, ShieldAlert, User as UserIcon, Clock } from "lucide-react";

/**
 * V1.37 · Ficha operativa del alumno para el profesor.
 * Reutiliza el endpoint `GET /api/students/{id}/appointment-context` que ya
 * agrega toda la información en una sola llamada.
 *
 * Bloques:
 *   1. Datos del alumno (permiso, estado, profesor habitual, tipo)
 *   2. Reporte de clases (C, R, P, NA, A, Rest)
 *   3. Historial de exámenes (teórico + práctico + resumen)
 *   4. Condiciones especiales (sólo chip; nunca detalles sensibles)
 */

const STUDENT_TYPE_LABEL = {
  recorrido_completo: "Recorrido completo",
  traslado: "Traslado",
  recuperacion_puntos: "Recuperación de puntos",
  reciclaje: "Reciclaje",
  otros: "Otros",
};

const RESULT_LABEL = {
  apto: { text: "Apto", cls: "text-emerald-700", icon: CheckCircle2 },
  no_apto: { text: "No apto", cls: "text-red-700", icon: XCircle },
  no_presentado: { text: "No presentado", cls: "text-neutral-500", icon: XCircle },
};

// V1.37 · Disponibilidad semanal del alumno — orden L→D
const WEEKDAYS = [
  { key: "monday",    label: "Lun" },
  { key: "tuesday",   label: "Mar" },
  { key: "wednesday", label: "Mié" },
  { key: "thursday",  label: "Jue" },
  { key: "friday",    label: "Vie" },
  { key: "saturday",  label: "Sáb" },
  { key: "sunday",    label: "Dom" },
];

function AvailabilityBlock({ availability }) {
  if (!availability) return null;
  const weekly = availability.weekly_slots || {};
  const prefs = (availability.preferences || "").trim();
  const anySlots = WEEKDAYS.some((d) => (weekly[d.key] || []).length > 0);
  return (
    <div className="space-y-2" data-testid="teacher-block-availability">
      <div className="text-[11px] uppercase tracking-wider font-semibold text-neutral-500 flex items-center gap-1">
        <Clock className="h-3.5 w-3.5" /> Disponibilidad semanal
      </div>
      {!anySlots ? (
        <div className="text-xs text-neutral-500 italic">Sin disponibilidad registrada.</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
          {WEEKDAYS.map((d) => {
            const slots = weekly[d.key] || [];
            if (slots.length === 0) return null;
            return (
              <div
                key={d.key}
                className="flex items-start gap-2 text-xs rounded-lg border border-neutral-200 bg-neutral-50 px-2.5 py-1.5"
                data-testid={`teacher-avail-${d.key}`}
              >
                <span className="font-semibold text-neutral-700 w-8 shrink-0">{d.label}</span>
                <span className="text-neutral-600 tabular-nums">
                  {slots.map((s, i) => (
                    <span key={i} className={i > 0 ? "ml-2" : ""}>
                      {(s.start || "?")}–{(s.end || "?")}
                    </span>
                  ))}
                </span>
              </div>
            );
          })}
        </div>
      )}
      {prefs && (
        <div className="text-[11px] text-neutral-600 italic bg-amber-50 border border-amber-200 rounded-lg px-2 py-1.5" data-testid="teacher-avail-preferences">
          <span className="font-semibold not-italic text-amber-900">Preferencias:</span> {prefs}
        </div>
      )}
    </div>
  );
}

function BonoTiles({ bono }) {
  if (!bono) return null;
  const tiles = [
    { k: "C",    label: "Contratadas",  color: "bg-neutral-100 text-neutral-800" },
    { k: "R",    label: "Realizadas",   color: "bg-emerald-50 text-emerald-800" },
    { k: "P",    label: "Programadas",  color: "bg-amber-50 text-amber-800" },
    { k: "NA",   label: "No asistidas", color: "bg-red-50 text-red-800" },
    { k: "A",    label: "Ajustes",      color: "bg-blue-50 text-blue-800" },
    { k: "Rest", label: "Restantes",    color: "bg-orange-50 text-orange-900 font-bold" },
  ];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="teacher-bono-tiles">
      {tiles.map((t) => (
        <div key={t.k} className={`rounded-lg border border-neutral-200 px-3 py-2 ${t.color}`} data-testid={`teacher-bono-${t.k}`}>
          <div className="text-[10px] uppercase tracking-wider opacity-70">{t.label}</div>
          <div className="text-xl font-bold tabular-nums">{bono[t.k] ?? 0}</div>
        </div>
      ))}
    </div>
  );
}

function ExamHistoryBlock({ history }) {
  if (!history) return null;
  const rows = (arr, kind) => (arr || []).map((e, i) => {
    const r = RESULT_LABEL[e.result] || RESULT_LABEL.no_presentado;
    const Icon = r.icon;
    return (
      <div key={`${kind}-${i}`} className="flex items-center gap-2 text-sm" data-testid={`teacher-exam-${kind}-${i}`}>
        <span className="font-mono text-neutral-700 tabular-nums">{e.date}</span>
        <span className="text-neutral-400">→</span>
        <span className={`inline-flex items-center gap-1 font-semibold ${r.cls}`}>
          <Icon className="h-3.5 w-3.5" /> {r.text}
        </span>
      </div>
    );
  });
  const summary = (kind) => {
    const s = history.resumen?.[kind] || { apto: 0, no_apto: 0 };
    return `${s.no_apto} No apto${s.no_apto === 1 ? "" : "s"} · ${s.apto} Apto${s.apto === 1 ? "" : "s"}`;
  };
  return (
    <div className="space-y-3" data-testid="teacher-exam-history">
      <div>
        <div className="flex items-center justify-between mb-1">
          <div className="text-[11px] uppercase tracking-wider font-semibold text-neutral-600">
            Teórico
          </div>
          <div className="text-[11px] text-neutral-500 font-semibold" data-testid="teacher-exam-teorico-summary">
            {summary("teorico")}
          </div>
        </div>
        {rows(history.teorico, "teorico").length > 0
          ? <div className="space-y-0.5">{rows(history.teorico, "teorico")}</div>
          : <div className="text-xs text-neutral-500">Sin convocatorias.</div>}
      </div>
      <div>
        <div className="flex items-center justify-between mb-1">
          <div className="text-[11px] uppercase tracking-wider font-semibold text-neutral-600">
            Práctico
          </div>
          <div className="text-[11px] text-neutral-500 font-semibold" data-testid="teacher-exam-practico-summary">
            {summary("practico")}
          </div>
        </div>
        {rows(history.practico, "practico").length > 0
          ? <div className="space-y-0.5">{rows(history.practico, "practico")}</div>
          : <div className="text-xs text-neutral-500">Sin convocatorias.</div>}
      </div>
    </div>
  );
}

export default function TeacherOperationalPanel({ studentId: studentIdProp, onStudentChange }) {
  const [students, setStudents] = useState([]);
  const [studentIdInternal, setStudentIdInternal] = useState("");
  const studentId = studentIdProp !== undefined ? studentIdProp : studentIdInternal;
  const setStudentId = (v) => {
    if (onStudentChange) onStudentChange(v);
    else setStudentIdInternal(v);
  };
  const [context, setContext] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/students");
        if (cancelled) return;
        const list = Array.isArray(data) ? data : [];
        setStudents(list.filter((s) => s.active !== false));
      } catch (_) {
        if (!cancelled) setStudents([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!studentId) { setContext(null); return; }
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const { data } = await api.get(`/students/${studentId}/appointment-context`);
        if (!cancelled) setContext(data);
      } catch (_) {
        if (!cancelled) setContext(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [studentId]);

  const sortedStudents = useMemo(
    () => [...students].sort((a, b) => (a.name || "").localeCompare(b.name || "")),
    [students],
  );

  const s = context?.student;
  const fullName = s ? studentCalendarLabelWide(s) || s.name : "";

  return (
    <div className="card-surface p-4 space-y-4" data-testid="teacher-operational-panel">
      <div>
        <label className="text-sm font-semibold text-neutral-700 mb-1.5 block flex items-center gap-1.5">
          <UserIcon className="h-4 w-4" /> Ver ficha operativa del alumno
        </label>
        <select
          value={studentId}
          onChange={(e) => setStudentId(e.target.value)}
          data-testid="teacher-student-select"
          className="w-full h-11 rounded-xl border border-neutral-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
        >
          <option value="">— Selecciona un alumno —</option>
          {sortedStudents.map((st) => (
            <option key={st.id} value={st.id}>
              {studentCalendarLabelWide(st) || st.name}
            </option>
          ))}
        </select>
      </div>

      {studentId && loading && (
        <div className="text-sm text-neutral-500">Cargando ficha del alumno…</div>
      )}

      {studentId && !loading && !context && (
        <div className="text-sm text-red-600" data-testid="teacher-panel-error">
          No se pudo cargar el contexto del alumno.
        </div>
      )}

      {context && (
        <>
          {/* BLOQUE 1 · Datos del alumno */}
          <div className="rounded-xl border border-neutral-200 p-3 space-y-1.5" data-testid="teacher-block-datos">
            <div className="text-[11px] uppercase tracking-wider font-semibold text-neutral-500">
              Datos del alumno
            </div>
            <div className="text-lg font-bold" data-testid="teacher-student-name">{fullName}</div>
            <div className="flex flex-wrap gap-1.5 text-xs">
              {s.permit_type && (
                <span className="inline-flex items-center gap-1 rounded-full bg-neutral-100 px-2 py-0.5" data-testid="teacher-permit-type">
                  <span className="opacity-60">Permiso:</span>
                  <span className="font-semibold">{s.permit_type}</span>
                </span>
              )}
              {s.status && (
                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-800 px-2 py-0.5">
                  <span className="opacity-60">Estado:</span>
                  <span className="font-semibold">{s.status}</span>
                </span>
              )}
              {context.profesor_habitual && (
                <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 text-blue-800 px-2 py-0.5" data-testid="teacher-profesor-habitual">
                  <span className="opacity-60">Profesor:</span>
                  <span className="font-semibold">{context.profesor_habitual}</span>
                </span>
              )}
              <span className="inline-flex items-center gap-1 rounded-full bg-orange-50 text-orange-900 px-2 py-0.5" data-testid="teacher-student-type">
                <span className="opacity-60">Tipo:</span>
                <span className="font-semibold">
                  {STUDENT_TYPE_LABEL[s.student_type] || s.student_type || "—"}
                </span>
              </span>
            </div>
          </div>

          {/* BLOQUE 4 · Condiciones especiales (chip sólo) */}
          {s.has_special_conditions && (
            <div
              data-testid="teacher-block-conditions"
              className="rounded-xl border border-amber-300 bg-amber-50 px-3 py-2 flex items-start gap-2"
            >
              <ShieldAlert className="h-5 w-5 text-amber-700 shrink-0 mt-0.5" />
              <div>
                <div className="text-sm font-bold text-amber-900">Condiciones especiales</div>
                <div className="text-xs text-amber-800">
                  Consultar administración. La información detallada no se muestra por protección de datos.
                </div>
              </div>
            </div>
          )}

          {/* BLOQUE 2 · Reporte de clases */}
          <div className="rounded-xl border border-neutral-200 p-3 space-y-2" data-testid="teacher-block-bono">
            <div className="text-[11px] uppercase tracking-wider font-semibold text-neutral-500">
              Reporte de clases
            </div>
            <BonoTiles bono={context.bono} />
          </div>

          {/* BLOQUE 2b · Disponibilidad semanal del alumno */}
          <div className="rounded-xl border border-neutral-200 p-3">
            <AvailabilityBlock availability={context.availability} />
          </div>

          {/* BLOQUE 3 · Historial de exámenes */}
          <div className="rounded-xl border border-neutral-200 p-3 space-y-2" data-testid="teacher-block-examenes">
            <div className="text-[11px] uppercase tracking-wider font-semibold text-neutral-500 flex items-center gap-1">
              <GraduationCap className="h-3.5 w-3.5" /> Historial de exámenes
            </div>
            <ExamHistoryBlock history={context.exam_history} />
          </div>

          {context.incidents_active && context.incidents_active.length > 0 && (
            <div
              data-testid="teacher-incident"
              className="rounded-xl border border-red-300 bg-red-50 px-3 py-2 flex items-start gap-2"
            >
              <AlertTriangle className="h-5 w-5 text-red-700 shrink-0 mt-0.5" />
              <div className="text-sm text-red-900">
                <span className="font-bold">Incidencia activa</span> — consultar administración antes de la clase.
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
